import { useEffect, useRef, useState } from 'react'
import { cmsListOrFallback, cmsTextOrFallback } from '@/api/helpers'
import { cmsClamp, cmsParagraph, cmsTextTitle } from '@/styles/cmsText'

const DEFAULT_PROCESS_TITLE = 'A Clear Path from Chaos to Control'
const DEFAULT_PROCESS_DESCRIPTION =
  'We follow a straightforward, four-step process designed to deliver results efficiently and with complete transparency.'
const DEFAULT_PROCESS_STEPS = [
    {
      title: 'Discovery & Audit',
      description:
        'We start by listening. We learn about your business, your goals, and your operational pain points. Our initial audit identifies the true cost of data fragmentation in your current systems.',
    },
    {
      title: 'Strategy & Roadmap',
      description:
        'Based on the audit, we present a clear, practical strategy. You receive a detailed roadmap with transparent pricing, outlining how we will solve your most pressing challenge first.',
    },
    {
      title: 'Implementation & Build',
      description:
        'Our in-house team gets to work. We build and implement your solution with precision, providing you with regular updates and ensuring a smooth process with minimal disruption to your business.',
    },
    {
      title: 'Delivery & Partnership',
      description:
        "We deliver a fully functional solution ready to generate immediate value. But our work doesn't end there. We provide the support and partnership you need to ensure long-term success and prepare for future growth.",
    },
] as const

interface ProcessProps {
  isMobile: boolean
  cmsReady?: boolean
  title?: string
  description?: string
  steps?: Array<{ title: string; description: string }>
}

export default function Process({
  isMobile,
  cmsReady = true,
  title,
  description,
  steps,
}: ProcessProps) {
  const processTitle = cmsTextOrFallback(title, DEFAULT_PROCESS_TITLE, cmsReady)
  const processDescription = cmsTextOrFallback(description, DEFAULT_PROCESS_DESCRIPTION, cmsReady)
  const stepItems = steps?.length
    ? steps
    : [...cmsListOrFallback(undefined, DEFAULT_PROCESS_STEPS, cmsReady)]
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.1 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  if (isMobile) {
    return (
      <section
        id="process"
        ref={sectionRef}
        aria-label="Our process"
        style={{
          position: 'relative',
          width: '100%',
          height: '1555px',
          backgroundColor: '#000000',
          overflow: 'hidden',
        }}
      >
        {/* Headline */}
        <div
          className={`transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '8px',
            position: 'absolute',
            width: '364px',
            maxWidth: 'calc(100% - 32px)',
            height: '98px',
            left: '50%',
            transform: 'translateX(-50%)',
            top: '46px',
          }}
        >
          <h2
            title={cmsTextTitle(processTitle)}
            style={{
              width: '100%',
              height: '27px',
              fontFamily: "'Instrument Sans', sans-serif",
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '27px',
              textAlign: 'center',
              color: '#FFFFFF',
              margin: 0,
              ...cmsClamp(2),
            }}
          >
            {processTitle}
          </h2>
          <p
            title={cmsTextTitle(processDescription)}
            style={{
              width: '100%',
              fontFamily: "'Instrument Sans', sans-serif",
              fontWeight: 400,
              fontSize: '17px',
              lineHeight: '21px',
              textAlign: 'center',
              color: '#FFFFFF',
              margin: 0,
              ...cmsParagraph(),
            }}
          >
            {processDescription}
          </p>
        </div>

        {/* Services Cards Container */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0px',
            gap: '24px',
            position: 'absolute',
            width: '333px',
            left: '50%',
            transform: 'translateX(-50%)',
            top: '177px',
          }}
        >
          {/* Card 1 - Discovery & Audit */}
          <div
            className={`transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '10px',
              width: '333px',
              height: '320px',
              background: 'url("/images/lawless-capture-S_28CdEI9iM-unsplash 1.png"), #E2EFF8',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '32px',
            }}
          >
            <h3
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '24px',
                color: '#FFFFFF',
                margin: 0,
                marginBottom: '8px',
                ...cmsClamp(2),
              }}
            >
              {stepItems[0]?.title}
            </h3>
            <p
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 300,
                fontSize: '19px',
                lineHeight: '21px',
                color: '#FFFFFF',
                margin: 0,
                ...cmsClamp(8),
              }}
            >
              {stepItems[0]?.description}
            </p>
          </div>

          {/* Card 2 - Strategy & Roadmap */}
          <div
            className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '10px',
              width: '333px',
              height: '320px',
              background: '#8DA7BB',
              borderRadius: '32px',
            }}
          >
            <h3
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '24px',
                color: '#FFFFFF',
                margin: 0,
                marginBottom: '8px',
                ...cmsClamp(2),
              }}
            >
              {stepItems[1]?.title}
            </h3>
            <p
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 300,
                fontSize: '19px',
                lineHeight: '21px',
                color: '#FFFFFF',
                margin: 0,
                ...cmsClamp(8),
              }}
            >
              {stepItems[1]?.description}
            </p>
          </div>

          {/* Card 3 - Implementation & Build */}
          <div
            className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '10px',
              width: '333px',
              height: '320px',
              background: '#7099BE',
              borderRadius: '32px',
            }}
          >
            <h3
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '24px',
                color: '#FFFFFF',
                margin: 0,
                marginBottom: '8px',
                ...cmsClamp(2),
              }}
            >
              {stepItems[2]?.title}
            </h3>
            <p
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 300,
                fontSize: '19px',
                lineHeight: '21px',
                color: '#FFFFFF',
                margin: 0,
                ...cmsClamp(8),
              }}
            >
              {stepItems[2]?.description}
            </p>
          </div>

          {/* Card 4 - Delivery & Partnership */}
          <div
            className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              padding: '24px',
              gap: '10px',
              width: '333px',
              height: '320px',
              background: 'linear-gradient(211.62deg, rgba(54, 146, 210, 0) -16.82%, rgba(54, 146, 210, 0.73) 82.85%), url("/images/a-chosen-soul-wZNJz5HVMGU-unsplash 1.png"), #E2EFF8',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '32px',
            }}
          >
            <h3
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '24px',
                color: '#FFFFFF',
                margin: 0,
                marginBottom: '8px',
                ...cmsClamp(2),
              }}
            >
              {stepItems[3]?.title}
            </h3>
            <p
              style={{
                width: '100%',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: 300,
                fontSize: '19px',
                lineHeight: '21px',
                color: '#FFFFFF',
                margin: 0,
                ...cmsClamp(8),
              }}
            >
              {stepItems[3]?.description}
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      id="process"
      ref={sectionRef}
      aria-label="Our process"
      style={{
        position: 'relative',
        width: '1920px',
        height: '1205px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#000000',
        marginTop: '-2px',
      }}
    >

      {/* Section Title */}
      <h2
        title={cmsTextTitle(processTitle)}
        className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        style={{
          position: 'absolute',
          width: '1200px',
          height: '59px',
          left: 'calc(50% - 1200px/2)',
          top: '110px',
          fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '59px',
          textAlign: 'center',
          color: '#FFFFFF',
          margin: 0,
          ...cmsClamp(2),
        }}
      >
        {processTitle}
      </h2>

      {/* Description */}
      <p
        title={cmsTextTitle(processDescription)}
        style={{
          position: 'absolute',
          width: '1200px',
          left: 'calc(50% - 1200px/2)',
          top: '180px',
          fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
          fontWeight: 400,
          fontSize: '28px',
          lineHeight: '34px',
          textAlign: 'center',
          color: '#FFFFFF',
          margin: 0,
          ...cmsParagraph(),
        }}
      >
        {processDescription}
      </p>

      {/* Cards Container - Frame 7970 */}
      <div
        style={{
          position: 'absolute',
          width: '883.2px',
          height: '779.33px',
          left: 'calc(50% - 883.2px/2 - 0.4px)',
          top: '330px',
        }}
      >
        {/* Card 1 - Discovery & Audit */}
        <div
          className={`transition-all duration-[2500ms] transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            }`}
          style={{
            position: 'absolute',
            width: '429.6px',
            height: '458px',
            left: '0px',
            top: '0px',
            background: '#E2EFF8',
            borderRadius: '32px',
            overflow: 'hidden',
          }}
        >
          <img
            src="/images/lawless-capture-S_28CdEI9iM-unsplash 1.png"
            alt="Discovery & Audit"
            style={{
              position: 'absolute',
              width: '430px',
              height: '458px',
              left: '0px',
              top: '0px',
              objectFit: 'cover',
            }}
          />
          <h3
            style={{
              position: 'absolute',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              width: '223px',
              height: '92px',
              left: '24px',
              top: '186px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '38px',
              lineHeight: '46px',
              color: '#FFFFFF',
              margin: 0,
            }}
          >
            {stepItems[0]?.title}
          </h3>
          <p
            style={{
              position: 'absolute',
              width: '383px',
              height: '120px',
              left: '24px',
              top: '305px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '24px',
              color: '#FFFFFF',
              margin: 0,
              ...cmsClamp(5),
            }}
          >
            {stepItems[0]?.description}
          </p>
        </div>

        {/* Card 2 - Strategy & Roadmap */}
        <div
          className={`transition-all duration-[2500ms] transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            }`}
          style={{
            position: 'absolute',
            width: '429.6px',
            height: '297px',
            left: '453.6px',
            top: '0px',
            background: '#8DA7BB',
            borderRadius: '32px',
            transitionDelay: '300ms',
          }}
        >
          <h3
            style={{
              position: 'absolute',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              width: '223px',
              height: '92px',
              left: '24px',
              top: '46px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '38px',
              lineHeight: '46px',
              color: '#FFFFFF',
              margin: 0,
            }}
          >
            {stepItems[1]?.title}
          </h3>
          <p
            style={{
              position: 'absolute',
              width: '383px',
              height: '120px',
              left: '24.4px',
              top: '149px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '24px',
              color: '#FFFFFF',
              margin: 0,
              ...cmsClamp(5),
            }}
          >
            {stepItems[1]?.description}
          </p>
        </div>

        {/* Card 3 - Delivery & Partnership */}
        <div
          className={`transition-all duration-[2500ms] transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            }`}
          style={{
            position: 'absolute',
            width: '429px',
            height: '458px',
            left: '453.6px',
            top: '321.33px',
            background: '#E2EFF8',
            borderRadius: '32px',
            overflow: 'hidden',
            transitionDelay: '600ms',
          }}
        >
          <img
            src="/images/a-chosen-soul-wZNJz5HVMGU-unsplash 1.png"
            alt="Delivery & Partnership"
            style={{
              position: 'absolute',
              width: '429px',
              height: '458px',
              left: 'calc(50% - 429px/2 + 0.4px)',
              top: 'calc(50% - 458px/2 - 0.33px)',
              objectFit: 'cover',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '429px',
              height: '458px',
              left: '0.4px',
              top: '-0.33px',
              background: 'linear-gradient(211.62deg, rgba(54, 146, 210, 0) -16.82%, rgba(54, 146, 210, 0.73) 82.85%)',
              mixBlendMode: 'normal',
            }}
          />
          <h3
            style={{
              position: 'absolute',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              width: '223px',
              height: '92px',
              left: '23.4px',
              top: '163.67px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '38px',
              lineHeight: '46px',
              color: '#FFFFFF',
              margin: 0,
            }}
          >
            {stepItems[3]?.title}
          </h3>
          <p
            style={{
              position: 'absolute',
              width: '383px',
              height: '144px',
              left: '23.4px',
              top: '282.67px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '24px',
              color: '#FFFFFF',
              margin: 0,
              ...cmsClamp(6),
            }}
          >
            {stepItems[3]?.description}
          </p>
        </div>

        {/* Card 4 - Implementation & Build */}
        <div
          className={`transition-all duration-[2500ms] transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            }`}
          style={{
            position: 'absolute',
            width: '430px',
            height: '297.33px',
            left: '0px',
            top: '482px',
            background: '#7099BE',
            borderRadius: '32px',
            transitionDelay: '900ms',
          }}
        >
          <h3
            style={{
              position: 'absolute',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              width: '294px',
              height: '92px',
              left: '27px',
              top: '42px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '38px',
              lineHeight: '46px',
              color: '#FFFFFF',
              margin: 0,
            }}
          >
            {stepItems[2]?.title}
          </h3>
          <p
            style={{
              position: 'absolute',
              width: '383px',
              height: '120px',
              left: '27.4px',
              top: '145px',
              fontFamily: "'Instrument Sans', 'Segoe UI', sans-serif",
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '24px',
              color: '#FFFFFF',
              margin: 0,
              ...cmsClamp(5),
            }}
          >
            {stepItems[2]?.description}
          </p>
        </div>
      </div>
    </section>
  )
}
